import { AppEntitlementValue, expandAppEntitlementView } from 'lib/actions/apps/app-entitlement'
import { CancelFunc, emptyCancel } from 'lib/cancel'
import { NotifyClient } from 'lib/notify'
import { ActionReturn, DeleteReturn, GetReturn, ListReturn, Status } from 'lib/rpc'
import { Collector, expandValue, unaryInvoke, unaryListCollectSinglePage } from 'lib/rpcts'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppEntitlement, GetAppEntitlementRequest, GetAppEntitlementResponse } from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementsClient } from 'pbts/c1/api/app/v1/app_entitlement.client'
import {
    AppEntitlementProxy,
    AppEntitlementProxyView,
    CreateAppEntitlementProxyRequest,
    CreateAppEntitlementProxyResponse,
    DeleteAppEntitlementProxyRequest,
    EntitlementChainResponse,
    GetChildEntitlementsRequest,
    ListAppEntitlementsProxyResponse,
    SearchAppEntitlementProxyBindingsRequest,
    SearchAppEntitlementProxyBindingsResponse,
} from 'pbts/c1/api/app/v1/app_entitlement_proxy'
import { AppEntitlementsProxyClient, AppEntitlementsProxySearchClient } from 'pbts/c1/api/app/v1/app_entitlement_proxy.client'
import { Any } from 'pbts/google/protobuf/any'
import { Empty } from 'pbts/google/protobuf/empty'
import { Dispatch } from 'react'

export enum AppEntitlementProxyAction {
    create = 'AppProxyEntitlementsAction.create',
    delete = 'AppProxyEntitlementsAction.delete',
    search = 'AppProxyEntitlementsAction.search',
    childEntitlements = 'AppProxyEntitlementsAction.getChildEntitlements',
    sourceEntitlement = 'AppProxyEntitlementsAction.getSourceEntitlement',
    destinationEntitlement = 'AppProxyEntitlementsAction.getDestinationEntitlement',
}

export type AppEntitlementProxyActions = CreateAction
    | DeleteAction
    | SearchAction
    | GetChildEntitlements
    | GetSourceEntitlement
    | GetDestinationEntitlement

export type CreateAction = {
    type: AppEntitlementProxyAction.create
    payload: ActionReturn
}

export type DeleteAction = {
    type: AppEntitlementProxyAction.delete
    payload: DeleteReturn
}

export type SearchAction = {
    type: AppEntitlementProxyAction.search
    payload: ListReturn<AppEntitlementProxyValue>
}

export type GetChildEntitlements = {
    type: AppEntitlementProxyAction.childEntitlements
    payload: ListReturn<AppEntitlementValue>
}

export type GetSourceEntitlement = {
    type: AppEntitlementProxyAction.sourceEntitlement
    payload: GetReturn<AppEntitlementValue>
}

export type GetDestinationEntitlement = {
    type: AppEntitlementProxyAction.destinationEntitlement
    payload: GetReturn<AppEntitlementValue>
}

export interface AppEntitlementProxyValue extends AppEntitlementProxy {
    id: string
    displayName: string
    srcApp: App
    srcAppEntitlement: AppEntitlement
    dstApp: App
    dstAppEntitlement: AppEntitlement
}

export const expandAppEntitlementProxyView = (
    msg: AppEntitlementProxyView | undefined,
    values: Any[],
): AppEntitlementProxyValue => {
    const srcApp = expandValue<App>(msg?.srcAppPath || '', App, values)
    const srcAppEntitlement = expandValue<AppEntitlement>(msg?.srcAppEntitlementPath || '', AppEntitlement, values)
    const dstApp = expandValue<App>(msg?.dstAppPath || '', App, values)
    const dstAppEntitlement = expandValue<AppEntitlement>(msg?.dstAppEntitlementPath || '', AppEntitlement, values)

    return {
        ...AppEntitlementProxy.create(msg?.appProxyEntitlement ?? {}),
        srcApp: App.create({ ...srcApp }),
        srcAppEntitlement: AppEntitlement.create({ ...srcAppEntitlement }),
        dstApp: App.create({ ...dstApp }),
        dstAppEntitlement: AppEntitlement.create({ ...dstAppEntitlement }),
        id: `${srcAppEntitlement?.id}:${dstAppEntitlement?.id}`,
        displayName: `${srcAppEntitlement?.displayName} → ${dstAppEntitlement?.id}`,
    }
}

export class AppEntitlementProxyCollector extends Collector<AppEntitlementProxyValue> {
    push(resp: ListAppEntitlementsProxyResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): AppEntitlementProxyValue => {
                return expandAppEntitlementProxyView(v, resp.expanded)
            }),
        )
    }
}

export const searchAppEntitlementProxyBindings = (
    dispatch: Dispatch<AppEntitlementProxyActions>,
    client: AppEntitlementsProxySearchClient | undefined,
    request?: SearchAppEntitlementProxyBindingsRequest,
    notifyClient?: NotifyClient | undefined,
): CancelFunc => {
    if (!client || !request) {
        return emptyCancel
    }
    request.expandMask = { paths: ['*'] }
    request.pageToken = request.pageToken || ''
    const collector = new AppEntitlementProxyCollector()
    return unaryListCollectSinglePage<
        SearchAppEntitlementProxyBindingsRequest,
        SearchAppEntitlementProxyBindingsResponse
    >(
        request,
        client.searchAppEntitlementProxyBindings.bind(client),
        collector,
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    dispatch({
                        type: AppEntitlementProxyAction.search,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                            metadata: payload.metadata,
                        },
                    })
                    break
                default:
                    dispatch({
                        type: AppEntitlementProxyAction.search,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export class AppEntitlementChainCollector extends Collector<AppEntitlementValue> {
    push(resp: EntitlementChainResponse): void {
        this.buf = this.buf.concat(
            resp.list.map((v): AppEntitlementValue => {
                return expandAppEntitlementView(v, resp.expanded)
            }),
        )
    }
}

export const getChildEntitlements = (
    dispatch: Dispatch<AppEntitlementProxyActions>,
    client: AppEntitlementsProxySearchClient | undefined,
    request?: GetChildEntitlementsRequest,
    notifyClient?: NotifyClient | undefined,
): (() => void) | undefined => {
    if (!client || !request) {
        return emptyCancel
    }
    request.expandMask = { paths: ['*'] }
    const collector = new AppEntitlementChainCollector()
    return unaryInvoke<GetChildEntitlementsRequest, EntitlementChainResponse>(
        request,
        client.getChildEntitlements.bind(client),
        (payload) => {
            switch (payload.status) {
                case Status.OK:
                    collector.reset()
                    collector.push(payload.item)
                    dispatch({
                        type: AppEntitlementProxyAction.childEntitlements,
                        payload: {
                            status: Status.OK,
                            items: collector.buffer(),
                            metadata: payload.metadata,
                        },
                    })
                    break
                default:
                    dispatch({
                        type: AppEntitlementProxyAction.childEntitlements,
                        payload,
                    })
            }
        },
        notifyClient,
    )
}

export const getAppEntitlementForProxyBinding = (
    dispatch: Dispatch<AppEntitlementProxyActions>,
    client: AppEntitlementsClient | undefined,
    id: string,
    appId: string,
    actionType: AppEntitlementProxyAction.sourceEntitlement | AppEntitlementProxyAction.destinationEntitlement,
    notifyClient?: NotifyClient,
): (() => void) | undefined => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<GetAppEntitlementRequest, GetAppEntitlementResponse>(
        GetAppEntitlementRequest.create({
            id,
            appId,
            expandMask: { paths: ['*'] },
        }),
        client.get.bind(client),
        (payload) => {
            if (payload.status === Status.OK) {
                dispatch({
                    type: actionType,
                    payload: {
                        status: Status.OK,
                        item: expandAppEntitlementView(payload.item.appEntitlementView, payload.item.expanded),
                    },
                })
            } else {
                dispatch({
                    type: actionType,
                    payload,
                })
            }
        },
        notifyClient,
    )
}

export const createAppEntitlementProxy = (
    dispatch: Dispatch<AppEntitlementProxyActions>,
    client: AppEntitlementsProxyClient | undefined,
    srcAppId: string,
    srcAppEntitlementId: string,
    dstAppId: string,
    dstAppEntitlementId: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<CreateAppEntitlementProxyRequest, CreateAppEntitlementProxyResponse>(
        CreateAppEntitlementProxyRequest.create({
            srcAppId,
            srcAppEntitlementId,
            dstAppId,
            dstAppEntitlementId,
        }),
        client.create.bind(client),
        (payload) => {
            dispatch({
                type: AppEntitlementProxyAction.create,
                payload,
            })
        },
    )
}

export const deleteAppEntitlementProxy = (
    dispatch: Dispatch<AppEntitlementProxyActions>,
    client: AppEntitlementsProxyClient | undefined,
    srcAppId: string,
    srcAppEntitlementId: string,
    dstAppId: string,
    dstAppEntitlementId: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }

    return unaryInvoke<CreateAppEntitlementProxyRequest, Empty>(
        DeleteAppEntitlementProxyRequest.create({
            srcAppId,
            srcAppEntitlementId,
            dstAppId,
            dstAppEntitlementId,
        }),
        client.delete.bind(client),
        (payload) => {
            dispatch({
                type: AppEntitlementProxyAction.delete,
                payload,
            })
        },
    )
}
